/* Wave 12.18 (Vania 1 Oct) · /privacy — public, no sign-in required. */
import React from "react";
import LegalPage from "./LegalPage";

const MARKDOWN = `# watu wako Privacy Policy

Last updated: 1 October 2026

## Who we are

watu wako is a Nairobi events app. It lists events across Kenya, scores some of them, and lets members plan what to go to with their people. watu wako is run by Watu Wako Collective, Nairobi, Kenya. You can reach us at hello@watuwako.com.

This policy covers the watu wako app at app.watuwako.com, the website at watuwako.com, and the emails we send. It explains what we collect, why, who we share it with, and the choices you have. We follow Kenya's Data Protection Act, 2019.

## What we collect

We only collect what we need to run watu wako and show you events you'll like.

**When you sign up**

- If you continue with Google: your name, email address and profile photo. We do not get your Google password, contacts, calendar or anything else in your Google account.
- If you sign up with email: your email address, and the one-time code we send you to sign in.

**Your profile**

- Your display name, bio, photo and the area of Nairobi you hang out in.
- Your answers to the taste quiz, so we can suggest events.
- Your gender, only if you choose to tell us. You can pick "prefer not to say" or skip it.

**What you do on watu wako**

- Events you mark as going, interested or went, events you save, and organisers and venues you follow.
- Squads you join and messages you send in them.
- Reviews, ratings, posts, photos and videos you share.

**Membership and payments**

- Your membership and trial status.
- If you pay by M-Pesa, the phone number you pay from and the payment reference. We never see your M-Pesa PIN.

**Device and usage**

- Pages you view, searches you make, and ticket links you click.
- Your device type, browser, and the site that sent you to us.
- Your IP address, which we turn into a rough location (city level, not your exact address).

**Stored on your device**

- A sign-in token so you stay logged in, and your light or dark theme choice. We don't use advertising cookies.

## How we use it

- **To run your account:** sign you in, keep you signed in, and show your profile.
- **To recommend events:** your quiz answers, area, saves, follows and what you've been to help us pick events for you, including our pick.
- **To run the community:** show your reviews and posts, run squads, and award events based on member ratings.
- **To handle membership:** start and end trials, and take payments once paid membership goes live.
- **To talk to you:** send sign-in codes and important account messages. We'll only send news or offers if you've agreed to them, and you can stop them any time.
- **To improve watu wako:** see which pages and events people use, in totals and trends, and fix bugs.
- **To keep watu wako safe:** spot spam, fake accounts and abuse, and follow the law.

We use your information because you've asked us to run your account, because it's needed for the service to work, because you've agreed (for example, telling us your gender), or because we have a fair reason such as keeping the app safe.

### Google user data

If you sign in with Google, we receive only your name, email address and profile photo (the openid, email and profile permissions). We use this only to create your account and sign you in. We do not sell it, use it for advertising, or share it with anyone except the service providers listed below who help us run sign in. Our use of information received from Google follows the [Google API Services User Data Policy](https://developers.google.com/terms/api-services-user-data-policy), including the Limited Use requirements.

## What other people can see, and who we share with

**Public on watu wako**

- Your display name and profile photo.
- Reviews, ratings, posts, photos and videos you share publicly.
- Event listings, which are public for everyone.

Your email address, phone number, gender and messages are never shown on your profile.

**Service providers who help us run watu wako**

We share only what each one needs to do its job, and they may not use it for anything else.

| Who | What they do for us |
| --- | --- |
| Emergent | Hosts the watu wako app and its database |
| Netlify | Hosts the watuwako.com website |
| Google | Google sign in, and our work email |
| Resend | Sends sign-in codes and account emails |
| ip-api.com | Turns an IP address into a rough city location |
| Safaricom (M-Pesa) | Processes membership payments, once live |

**Other times we may share**

- If the law requires it, for example a valid request from a Kenyan court or authority.
- To protect members, watu wako or the public from harm or fraud.
- If watu wako is sold or merged, in which case this policy keeps applying to your information and we'll tell you first.

**We never sell your personal information**, and we don't share it with advertisers.

## Where we keep it, how long, and how we protect it

**Where.** Some of our service providers store information on servers outside Kenya, for example in the United States or Europe. When that happens we use providers that protect it to a standard that meets the Kenya Data Protection Act.

**How long.** We keep your information while your account is open. If you delete your account:

- your profile, follows, saves and messages are deleted within 30 days;
- reviews and ratings are kept without your name, so event awards stay fair;
- payment records are kept for 7 years because tax law requires it;
- usage logs are deleted after 12 months.

**How we protect it.** Connections to watu wako are encrypted, sign-in codes expire quickly, only the people who run watu wako can see member information, and we never store Google or M-Pesa passwords or PINs. No system is perfect, so if a breach ever puts your information at risk we'll tell you and the Data Commissioner as the law requires.

## Your rights

Under the Kenya Data Protection Act 2019 you can:

- **See it:** ask for a copy of the information we hold about you.
- **Fix it:** correct anything wrong. Most of your profile you can edit yourself in the app.
- **Delete it:** close your account and have your information deleted, apart from what the law makes us keep.
- **Object:** tell us to stop using your information for something, including any marketing.
- **Take it with you:** get your information in a format you can move elsewhere.
- **Change your mind:** take back consent you gave, like sharing your gender, at any time.

To use any of these, email **hello@watuwako.com** from the address on your account. We'll reply within 7 days and finish within 30 days.

If you're not happy with how we handled it, you can complain to the Office of the Data Protection Commissioner (ODPC) at [odpc.go.ke](https://www.odpc.go.ke).

## Children

watu wako is for people aged 18 and over. When you sign up, you confirm you're 18 or older. If we learn that someone under 18 has an account, we'll close it and delete their information. Parents can contact us at hello@watuwako.com.

## Changes to this policy

If we change this policy we'll update the date at the top. If the change is a big one, we'll tell you in the app or by email before it takes effect.

## Contact us

Questions about your privacy or this policy: **hello@watuwako.com**

Watu Wako Collective, Nairobi, Kenya.
`;

export default function Privacy() {
  return <LegalPage title="privacy policy · watu wako" markdown={MARKDOWN} />;
}
